import { z } from 'zod';
import {
  COURT_MAX_PLAYERS,
  COURT_MIN_PLAYERS,
  COURT_MAX_REJECTIONS,
  courtRoleSchema,
} from './court.js';
import { lookSchema } from './look.js';
import {
  BAZAAR_MARKET_SLOTS,
  BAZAAR_MAX_PLAYERS,
  BAZAAR_MIN_PLAYERS,
  BAZAAR_STASH_LIMIT,
  BAZAAR_TOKEN_LIMIT,
  BAZAAR_TURN_SECONDS,
  BAZAAR_WIN_POINTS,
  bazaarTokenSchema,
} from './bazaar.js';

/**
 * WebSocket messages for table games (board games, cards, mahjong).
 * Owned by the games feature. Every message type starts with `game:` so the
 * gateway routes it to the games module; keep that prefix.
 *
 * A table is a piece of furniture whose catalogue entry carries
 * `interaction: { type: 'game', game }`. Everyone in the room who opens the
 * table receives its state; players sit, start, and send moves.
 */

export const GAME_IDS = [
  'tictactoe',
  'connect4',
  'checkers',
  'xiangqi',
  'snakes',
  'poker',
  'mahjong',
  'court',
  'bazaar',
  'floatingbridge',
] as const;
export const gameIdSchema = z.enum(GAME_IDS);
export type GameId = z.infer<typeof gameIdSchema>;

/** Floating Bridge: a hand's points (A=4, K=3, Q=2, J=1, plus 1 per card beyond 4 in any suit) at or below this may call for a reshuffle. */
export const FLOATINGBRIDGE_RESHUFFLE_MAX_POINTS = 3;

export const GAME_INFO: Readonly<Record<GameId, { label: string; minPlayers: number; maxPlayers: number }>> =
  Object.freeze({
    tictactoe: { label: 'Tic-tac-toe', minPlayers: 2, maxPlayers: 2 },
    connect4: { label: 'Connect Four', minPlayers: 2, maxPlayers: 2 },
    checkers: { label: 'Checkers (Dam)', minPlayers: 2, maxPlayers: 2 },
    xiangqi: { label: 'Chinese Chess', minPlayers: 2, maxPlayers: 2 },
    snakes: { label: 'Snakes & Ladders', minPlayers: 2, maxPlayers: 8 },
    poker: { label: "Texas Hold'em", minPlayers: 2, maxPlayers: 6 },
    mahjong: { label: 'Mahjong', minPlayers: 4, maxPlayers: 4 },
    court: { label: 'Shadow Court', minPlayers: COURT_MIN_PLAYERS, maxPlayers: COURT_MAX_PLAYERS },
    bazaar: { label: 'Spice Bazaar', minPlayers: BAZAAR_MIN_PLAYERS, maxPlayers: BAZAAR_MAX_PLAYERS },
    floatingbridge: { label: 'Floating Bridge', minPlayers: 4, maxPlayers: 4 },
  });

/**
 * Short description and how-to-play steps for every game, shown in the table
 * window and the Games list.
 *
 * A table seats only as many as the rules allow, but a room can hold as many
 * tables as it has space for, and anyone in the room can watch any table.
 */
export const GAME_GUIDE: Readonly<Record<GameId, { summary: string; steps: readonly string[] }>> = Object.freeze({
  tictactoe: {
    summary: 'The classic. Three in a row wins.',
    steps: [
      'Two players. The first to move is X, the other is O.',
      'Take turns marking an empty square.',
      'Get three of your marks in a row, across, down or diagonally, to win.',
      'If the board fills up with no line, it is a draw.',
    ],
  },
  connect4: {
    summary: 'Drop discs and line up four before your kaki does.',
    steps: [
      'Two players, Red and Yellow, take turns.',
      'Click a column to drop a disc. It falls to the lowest empty space.',
      'Line up four of your discs across, down or diagonally to win.',
      'A full board with no line is a draw.',
    ],
  },
  checkers: {
    summary: 'Dam, like at the void deck. Captures are compulsory.',
    steps: [
      'Two players. Pieces move one square diagonally forward.',
      'Jump over an opponent piece to capture it. If you can capture, you must.',
      'Keep jumping in the same turn while more captures are open.',
      'Reach the far row to become a king, which moves both ways.',
      'Capture every enemy piece, or leave them with no moves, to win.',
    ],
  },
  xiangqi: {
    summary: "Chinese chess, the uncles' favourite. Protect your general.",
    steps: [
      'Two players, Red and Black. Red moves first.',
      'Horses can be blocked, elephants cannot cross the river, and cannons capture by jumping exactly one piece.',
      'The two generals may never face each other on an open file.',
      'Put the enemy general in check with no escape (checkmate) to win.',
      'Repeating checks forever loses.',
    ],
  },
  snakes: {
    summary: 'Roll and pray. Ladders lift you, snakes drop you.',
    steps: [
      'Two to eight players share the board. Everyone starts off the board.',
      'On your turn press Roll. The server rolls the die for you.',
      'Land on a ladder to climb it, or on a snake head to slide down.',
      'You must land exactly on 100. If you overshoot, you bounce back.',
      'Run out of time and your die is rolled for you.',
    ],
  },
  poker: {
    summary: "No-limit Texas Hold'em for fun chips.",
    steps: [
      'Two to six players. Everyone starts with 1,000 fun chips, and blinds go up every 8 hands.',
      'You get two private cards. Five shared cards are dealt in three rounds: flop, turn and river.',
      'On your turn you can fold, check, call, bet, raise or go all-in.',
      'Make the best five-card hand from your two cards and the five shared ones, or make everyone else fold.',
      'Last player with chips wins. Chips have no value and never pay Kaki Dollars.',
    ],
  },
  mahjong: {
    summary: 'Singapore mahjong for four, with flowers and animals.',
    steps: [
      'Four players, 500 fun chips each, one wind round per game.',
      'Each turn you draw a tile and discard one. Flowers and animals count as bonus tai.',
      'Make four sets (chow, pong or kong) and a pair to win. Seven pairs does not count.',
      'Anyone can pong or kong a discard. Only the player on your left can chow. A win beats every other claim.',
      'Tai come from flowers, animals, dragon and wind pongs, all pongs, flushes and self-draw, capped at 5.',
    ],
  },
  court: {
    summary: 'Hidden roles, secret votes and missions. Good must find the traitors; evil must hide.',
    steps: [
      `${COURT_MIN_PLAYERS} to ${COURT_MAX_PLAYERS} players. Each secretly gets a role, good or evil. Good has more players; evil know each other.`,
      'Play goes over up to five missions. Each round the leader picks a team for the mission, and everyone votes to approve or reject it. Votes are public.',
      `If a team is rejected, the leader passes to the next player. ${COURT_MAX_REJECTIONS} rejections in a row and evil wins.`,
      'An approved team goes on the mission. Each member secretly plays Success or Fail. Good must play Success; evil may play Fail. One Fail sinks a mission (two on mission 4 with 7 or more players).',
      'Three failed missions and evil wins.',
      'If good completes three missions, the Hunter gets one guess at who Seer is. Hit Seer and evil steals the win.',
      'Use the room chat to talk, accuse and bluff. Seer knows who is evil but must not be too obvious. Guardian sees Seer and Mimic but not which is which.',
    ],
  },
  bazaar: {
    summary: 'Collect spices, buy stall cards and win the favour of patrons. First to 15 fame points ends the game.',
    steps: [
      `${BAZAAR_MIN_PLAYERS} to ${BAZAAR_MAX_PLAYERS} players. Each turn you do exactly ONE of four things, then press Ready to pass the turn on (or wait out the ${BAZAAR_TURN_SECONDS / 60}-minute timer).`,
      'Take three spices of different colours. If fewer than three colours are left, take one of each that remains.',
      'Or take two spices of the same colour, but only if four or more of that colour are in the bank.',
      `Or stash one card, face-up or from the top of a deck, and take one wild. You may hold ${BAZAAR_STASH_LIMIT} stashed cards, and the wild is skipped if none is left.`,
      'Or buy a face-up card or one you stashed. Pay its spices, less a discount of one spice for every card of that colour you own. Wild counts as any colour.',
      `You may never end a turn holding more than ${BAZAAR_TOKEN_LIMIT} spices, wild included: return the extras.`,
      'At the end of your turn, if your cards match a patron, that patron visits you for 3 points. If you match several, choose one. Only one patron visits per turn.',
      `When someone reaches ${BAZAAR_WIN_POINTS} points, everyone finishes the round so all have had the same number of turns. Most points wins. A tie goes to whoever bought the fewest cards, and a tie after that is shared.`,
      'Run out of the turn timer and your turn is skipped (or finished for you if you already acted). Three skipped turns in a row knocks you out.',
    ],
  },
  floatingbridge: {
    summary: 'A kopitiam classic. Bid for trump, call a secret partner, and win the trick war.',
    steps: [
      'Four players, standard 52-card deck, Ace high. Everyone gets 13 cards.',
      'Bidding: in turn, bid a number (1-7) and a suit or No Trump, each bid higher than the last, or pass. Bidding ends when three players pass in a row. If all four pass, the cards are redealt.',
      `Points: Ace = 4, King = 3, Queen = 2, Jack = 1, plus 1 for every card beyond 4 in a suit. ${FLOATINGBRIDGE_RESHUFFLE_MAX_POINTS} points or fewer, and on your turn you may call for a reshuffle instead of bidding or passing — the whole hand is redealt, however far the bidding has gone.`,
      'The highest bidder is the declarer and must win their bid number + 6 tricks in that trump (or in No Trump, highest card of the suit led always wins).',
      'The declarer then calls any card they do not hold. Whoever holds it is their secret partner for this hand, but nobody is told who: it floats, and is only revealed once that exact card is played.',
      'The declarer leads the first trick. Follow suit if you can; otherwise play anything, trump included. Highest trump wins the trick, or the highest card of the suit led if no trump was played.',
      'After all 13 tricks: if the declarer and their floating partner together won enough tricks to make the bid, they win the hand. Otherwise the other two win.',
    ],
  },
});

/** Games where empty seats can be filled by computer players ("Fill with bots"), and that replace a leaver. */
export const BOT_GAMES: readonly GameId[] = ['poker', 'mahjong', 'court', 'floatingbridge'];
export const acceptsBots = (game: GameId): boolean => BOT_GAMES.includes(game);

/**
 * Card and tile games are played for PLAY CHIPS only: a fresh stack per table
 * session, worth nothing, never bought, transferred or converted, and gone
 * when you stand up. They never pay Kaki Dollars. They also accept bots.
 */
export const CHIP_GAMES: readonly GameId[] = ['poker', 'mahjong'];
export const isChipGame = (game: GameId): boolean => CHIP_GAMES.includes(game);

/** Seconds a player has for each turn. */
export const GAME_TURN_SECONDS = 45;
/** Seconds a seated player may be out of the room before forfeiting. */
export const GAME_ABSENCE_GRACE_SECONDS = 20;
/** Kaki Dollars for a rewarded win, and the daily caps on rewards. */
export const GAME_WIN_REWARD = 50;
export const GAME_REWARDS_PER_DAY = 5;
export const GAME_REWARDS_PER_OPPONENT_PER_DAY = 2;
/** A game must have this many moves in total before a win pays out. */
export const GAME_MIN_MOVES_FOR_REWARD = 5;

/* -------------------------------------------------------------------------- */
/* Board geometry shared by client and server                                 */
/* -------------------------------------------------------------------------- */

export const CONNECT4_COLUMNS = 7;
export const CONNECT4_ROWS = 6;

/** A square on the 8x8 checkers board; row 0 is the top (player 1's back row). */
const checkersSquare = z.object({ r: z.number().int().min(0).max(7), c: z.number().int().min(0).max(7) });

/** A point on the xiangqi board: x = file 0-8, y = rank 0-9 (0 is black's back rank). */
const xiangqiPoint = z.object({ x: z.number().int().min(0).max(8), y: z.number().int().min(0).max(9) });

export const XIANGQI_PIECES = ['general', 'advisor', 'elephant', 'horse', 'chariot', 'cannon', 'soldier'] as const;

/**
 * The classic 1943 snakes-and-ladders layout (Milton Bradley's "Chutes and
 * Ladders"). Squares are numbered 1-100 in boustrophedon order from the
 * bottom-left.
 */
export const SNAKES_LADDERS: readonly { from: number; to: number }[] = [
  { from: 1, to: 38 },
  { from: 4, to: 14 },
  { from: 9, to: 31 },
  { from: 21, to: 42 },
  { from: 28, to: 84 },
  { from: 36, to: 44 },
  { from: 51, to: 67 },
  { from: 71, to: 91 },
  { from: 80, to: 100 },
];

export const SNAKES_SNAKES: readonly { from: number; to: number }[] = [
  { from: 16, to: 6 },
  { from: 47, to: 26 },
  { from: 49, to: 11 },
  { from: 56, to: 53 },
  { from: 62, to: 19 },
  { from: 64, to: 60 },
  { from: 87, to: 24 },
  { from: 93, to: 73 },
  { from: 95, to: 75 },
  { from: 98, to: 78 },
];

/* -------------------------------------------------------------------------- */
/* Poker: Texas Hold'em                                                       */
/* -------------------------------------------------------------------------- */

export const POKER_START_CHIPS = 1000;
/** Small/big blinds; the level rises every POKER_HANDS_PER_LEVEL hands. */
export const POKER_BLIND_LEVELS: ReadonlyArray<readonly [small: number, big: number]> = [
  [10, 20],
  [15, 30],
  [25, 50],
  [50, 100],
  [75, 150],
  [100, 200],
  [150, 300],
  [250, 500],
];
export const POKER_HANDS_PER_LEVEL = 8;
export const POKER_TURN_SECONDS = 30;
/** Pause after a showdown before the next hand is dealt. */
export const POKER_BETWEEN_SECONDS = 6;

/**
 * Cards are two characters: rank ("23456789TJQKA") then suit ("s", "h", "d",
 * "c"), e.g. "As" = ace of spades, "Td" = ten of diamonds.
 */
export const CARD_RANKS = '23456789TJQKA';
export const CARD_SUITS = 'shdc';
const cardCode = z.string().regex(/^[2-9TJQKA][shdc]$/);

export const POKER_ACTIONS = ['fold', 'check', 'call', 'bet', 'raise', 'allin'] as const;

/* -------------------------------------------------------------------------- */
/* Mahjong: Singapore style                                                   */
/* -------------------------------------------------------------------------- */

export const MAHJONG_START_CHIPS = 500;
export const MAHJONG_TAI_CAP = 5;
export const MAHJONG_TURN_SECONDS = 25;
export const MAHJONG_CLAIM_SECONDS = 10;
export const MAHJONG_BETWEEN_SECONDS = 7;
/** Chips per unit at each tai (0..cap): 4 x 2^tai. */
export const MAHJONG_UNIT_CHIPS: readonly number[] = [4, 8, 16, 32, 64, 128];

/**
 * Tile codes (148 tiles):
 * - "B1".."B9" bamboo, "C1".."C9" characters, "D1".."D9" dots (4 of each)
 * - "WE" "WS" "WW" "WN" winds, "RD" "GD" "WD" red/green/white dragons (4 each)
 * - "F1".."F4" flowers, "S1".."S4" seasons (the number is the matching seat)
 * - "AC" cat, "AR" rat, "AK" rooster, "AP" centipede
 */
export const MAHJONG_SUITS = ['B', 'C', 'D'] as const;
export const MAHJONG_WINDS = ['WE', 'WS', 'WW', 'WN'] as const;
export const MAHJONG_DRAGONS = ['RD', 'GD', 'WD'] as const;
export const MAHJONG_FLOWERS = ['F1', 'F2', 'F3', 'F4', 'S1', 'S2', 'S3', 'S4'] as const;
export const MAHJONG_ANIMALS = ['AC', 'AR', 'AK', 'AP'] as const;
const tileCode = z.string().regex(/^([BCD][1-9]|W[ESWN]|[RGW]D|[FS][1-4]|A[CRKP])$/);

export const MAHJONG_ACTIONS = ['discard', 'pass', 'pong', 'kong', 'chow', 'win'] as const;

/* -------------------------------------------------------------------------- */
/* Floating Bridge                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Bid strains, ranked low to high (clubs lowest, No Trump highest): a bid of
 * the same number in a higher strain outranks one in a lower strain.
 */
export const FLOATINGBRIDGE_STRAINS = ['c', 'd', 'h', 's', 'n'] as const;
export type FloatingBridgeStrain = (typeof FLOATINGBRIDGE_STRAINS)[number];
export const FLOATINGBRIDGE_ACTIONS = ['bid', 'pass', 'call', 'play', 'reshuffle'] as const;

/* -------------------------------------------------------------------------- */
/* Moves                                                                      */
/* -------------------------------------------------------------------------- */

export const gameMoveSchema = z.discriminatedUnion('game', [
  z.object({ game: z.literal('tictactoe'), cell: z.number().int().min(0).max(8) }),
  z.object({ game: z.literal('connect4'), column: z.number().int().min(0).max(CONNECT4_COLUMNS - 1) }),
  /** The moving piece's square, then every landing square (one for a step, several for a multi-jump). */
  z.object({ game: z.literal('checkers'), path: z.array(checkersSquare).min(2).max(13) }),
  z.object({ game: z.literal('xiangqi'), from: xiangqiPoint, to: xiangqiPoint }),
  z.object({ game: z.literal('snakes'), action: z.literal('roll') }),
  /** `amount` is the total bet to reach this street (for bet and raise). */
  z.object({
    game: z.literal('poker'),
    action: z.enum(POKER_ACTIONS),
    amount: z.number().int().min(0).max(10_000_000).optional(),
  }),
  /**
   * discard: `tile`. kong on your own turn: `tile` names the concealed or added
   * kong; kong on a discard needs no tile. chow: `tiles` are the two tiles from
   * your hand that make a run with the discard.
   */
  z.object({
    game: z.literal('mahjong'),
    action: z.enum(MAHJONG_ACTIONS),
    tile: tileCode.optional(),
    tiles: z.array(tileCode).length(2).optional(),
  }),
  /**
   * propose: `team` (the leader's picks, by player index). vote: `approve`.
   * mission: `success` (good must play success). expose: `target`.
   */
  z.object({
    game: z.literal('court'),
    action: z.enum(['propose', 'vote', 'mission', 'expose']),
    team: z.array(z.number().int().min(0).max(COURT_MAX_PLAYERS - 1)).max(COURT_MAX_PLAYERS).optional(),
    approve: z.boolean().optional(),
    success: z.boolean().optional(),
    target: z.number().int().min(0).max(COURT_MAX_PLAYERS - 1).optional(),
  }),
  /**
   * take: `tokens` are the spice colours. stash: a face-up `card` id, or a
   * `level` to take the top card of that deck blind. buy: a face-up or
   * stashed `card` id. discard: `tokens` to return (wild allowed). patron:
   * the `patron` to accept. ready: end your turn. pass: only when no other
   * action is possible.
   */
  z.object({
    game: z.literal('bazaar'),
    action: z.enum(['take', 'stash', 'buy', 'discard', 'patron', 'ready', 'pass']),
    tokens: z.array(bazaarTokenSchema).max(BAZAAR_TOKEN_LIMIT + 3).optional(),
    card: z.number().int().min(0).max(89).optional(),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    patron: z.number().int().min(0).max(9).optional(),
  }),
  /**
   * bid: `number` (1-7) and `strain`. pass: bidding only. reshuffle: bidding
   * only, and only when the mover's hand scores FLOATINGBRIDGE_RESHUFFLE_MAX_POINTS
   * or fewer -- redeals for everyone. call: the declarer's chosen `card`
   * (naming the floating partner, once bidding ends). play: a `card` from
   * your hand.
   */
  z.object({
    game: z.literal('floatingbridge'),
    action: z.enum(FLOATINGBRIDGE_ACTIONS),
    number: z.number().int().min(1).max(7).optional(),
    strain: z.enum(FLOATINGBRIDGE_STRAINS).optional(),
    card: cardCode.optional(),
  }),
]);
export type GameMove = z.infer<typeof gameMoveSchema>;

/* -------------------------------------------------------------------------- */
/* Views (what a viewer is allowed to see of a game in progress)              */
/* -------------------------------------------------------------------------- */

const playerIndex = z.number().int().min(0).max(COURT_MAX_PLAYERS - 1);
/** Six token counts: white, blue, green, red, black, wild. */
const bazaarTokenCounts = z.array(z.number().int().min(0)).length(6);

export const gameViewSchema = z.discriminatedUnion('game', [
  z.object({
    game: z.literal('tictactoe'),
    /** Owner of each cell by player index, null when empty. */
    cells: z.array(playerIndex.nullable()).length(9),
    lastCell: z.number().int().nullable(),
    winLine: z.array(z.number().int()).nullable(),
  }),
  z.object({
    game: z.literal('connect4'),
    /** Row-major, row 0 at the top. */
    grid: z.array(playerIndex.nullable()).length(CONNECT4_COLUMNS * CONNECT4_ROWS),
    last: z.object({ column: z.number().int(), row: z.number().int() }).nullable(),
    winLine: z.array(z.object({ column: z.number().int(), row: z.number().int() })).nullable(),
  }),
  z.object({
    game: z.literal('checkers'),
    pieces: z.array(z.object({ r: z.number().int(), c: z.number().int(), owner: playerIndex, king: z.boolean() })),
    /** Every legal move for the player to move (each a full path). */
    legalMoves: z.array(z.array(checkersSquare)),
    mustCapture: z.boolean(),
    lastMove: z.array(checkersSquare).nullable(),
  }),
  z.object({
    game: z.literal('xiangqi'),
    pieces: z.array(z.object({ x: z.number().int(), y: z.number().int(), side: playerIndex, type: z.enum(XIANGQI_PIECES) })),
    legalMoves: z.array(z.object({ from: xiangqiPoint, to: xiangqiPoint })),
    inCheck: z.boolean(),
    lastMove: z.object({ from: xiangqiPoint, to: xiangqiPoint }).nullable(),
  }),
  z.object({
    game: z.literal('snakes'),
    /** Square per player (0 = not on the board yet). */
    positions: z.array(z.number().int().min(0).max(100)),
    /** Players knocked out (forfeit); their tokens are hidden. */
    out: z.array(z.boolean()),
    lastRoll: z
      .object({ player: playerIndex, value: z.number().int().min(1).max(6), from: z.number().int(), to: z.number().int() })
      .nullable(),
  }),
  z.object({
    game: z.literal('poker'),
    /** Hand number (1-based); the commitment below is for this hand's deck. */
    hand: z.number().int(),
    commitment: z.string(),
    phase: z.enum(['preflop', 'flop', 'turn', 'river', 'between']),
    button: playerIndex,
    blinds: z.tuple([z.number().int(), z.number().int()]),
    community: z.array(cardCode),
    /** Main pot then side pots, including chips bet on this street. */
    pots: z.array(z.object({ amount: z.number().int(), eligible: z.array(playerIndex) })),
    players: z.array(
      z.object({
        chips: z.number().int(),
        /** Put in on this betting round. */
        bet: z.number().int(),
        folded: z.boolean(),
        allIn: z.boolean(),
        /** Busted or left: sits out for the rest of the match. */
        out: z.boolean(),
        /** Still has hole cards this hand. */
        inHand: z.boolean(),
        /** Only the viewer's own cards, or everyone's shown at a showdown. */
        cards: z.array(cardCode).nullable(),
        lastAction: z.string().nullable(),
      }),
    ),
    toAct: playerIndex.nullable(),
    /** Options for the viewer when it is their turn, else null. */
    options: z
      .object({
        canCheck: z.boolean(),
        callAmount: z.number().int(),
        canRaise: z.boolean(),
        /** Total bets to reach ("raise to"), bounded by the viewer's stack. */
        minRaiseTo: z.number().int(),
        maxRaiseTo: z.number().int(),
      })
      .nullable(),
    lastHand: z
      .object({
        hand: z.number().int(),
        winners: z.array(z.object({ player: playerIndex, amount: z.number().int(), handName: z.string() })),
        shown: z.array(z.object({ player: playerIndex, cards: z.array(cardCode), handName: z.string() })),
        community: z.array(cardCode),
      })
      .nullable(),
  }),
  z.object({
    game: z.literal('mahjong'),
    round: z.number().int(),
    commitment: z.string(),
    phase: z.enum(['turn', 'claim', 'between']),
    /** Prevailing wind (0 = East). */
    prevailingWind: z.number().int().min(0).max(3),
    dealer: playerIndex,
    wallCount: z.number().int(),
    players: z.array(
      z.object({
        chips: z.number().int(),
        /** Seat wind, 0 = East. */
        wind: z.number().int().min(0).max(3),
        handCount: z.number().int(),
        /** The viewer's own concealed tiles only. */
        hand: z.array(tileCode).nullable(),
        melds: z.array(z.object({ type: z.enum(['chow', 'pong', 'kong']), tiles: z.array(tileCode), concealed: z.boolean() })),
        bonus: z.array(tileCode),
        discards: z.array(tileCode),
      }),
    ),
    /** Player whose turn it is to discard (or draw), null during a claim window. */
    toAct: playerIndex.nullable(),
    /** The viewer's just-drawn tile, so the UI can highlight it. */
    drawn: tileCode.nullable(),
    lastDiscard: z.object({ player: playerIndex, tile: tileCode }).nullable(),
    /** What the viewer may do right now. */
    options: z
      .object({
        discard: z.boolean(),
        win: z.boolean(),
        pong: z.boolean(),
        /** Kong on the discard. */
        kong: z.boolean(),
        /** Concealed or added kongs available on your own turn. */
        selfKongs: z.array(tileCode),
        /** Pairs of hand tiles that make a chow with the discard. */
        chows: z.array(z.array(tileCode).length(2)),
        pass: z.boolean(),
      })
      .nullable(),
    /** Players who have answered the current claim window. */
    responded: z.array(playerIndex),
    lastRound: z
      .object({
        round: z.number().int(),
        winner: playerIndex.nullable(),
        /** Who discarded the winning tile; null for self-draw or a drawn game. */
        discarder: playerIndex.nullable(),
        selfDraw: z.boolean(),
        tai: z.number().int(),
        breakdown: z.array(z.object({ label: z.string(), tai: z.number().int() })),
        /** Chip change per player. */
        payments: z.array(z.number().int()),
        hand: z.array(tileCode),
        melds: z.array(z.object({ type: z.enum(['chow', 'pong', 'kong']), tiles: z.array(tileCode), concealed: z.boolean() })),
        bonus: z.array(tileCode),
      })
      .nullable(),
  }),
  z.object({
    game: z.literal('court'),
    /** Identifies this deal; the commitment's first eight hex digits as a number. */
    deal: z.number().int(),
    commitment: z.string(),
    phase: z.enum(['team', 'vote', 'mission', 'hunter', 'over']),
    /** Mission being played, 0-4. */
    mission: z.number().int().min(0).max(4),
    leader: playerIndex,
    /** Teams rejected in a row. */
    rejections: z.number().int().min(0).max(COURT_MAX_REJECTIONS),
    teamSize: z.number().int(),
    needsTwoFails: z.boolean(),
    /** The team being voted on, or on the mission. */
    team: z.array(playerIndex),
    missionSizes: z.array(z.number().int()).length(5),
    /** Finished missions, in order. Only the number of fails is public, never who played them. */
    results: z.array(z.object({ team: z.array(playerIndex), fails: z.number().int(), success: z.boolean() })),
    /** Who has voted on the current team (not how). */
    voted: z.array(playerIndex),
    /** The last vote, revealed once everyone has voted. */
    lastVote: z
      .object({ leader: playerIndex, team: z.array(playerIndex), approve: z.array(playerIndex), reject: z.array(playerIndex), passed: z.boolean() })
      .nullable(),
    /** Team members who have played their mission card (not which). */
    played: z.array(playerIndex),
    /** The viewer's own role and what it lets them see; null for spectators. */
    you: z
      .object({
        role: courtRoleSchema,
        evil: z.boolean(),
        sees: z.array(z.object({ player: playerIndex, as: z.enum(['evil', 'seer', 'seer_or_mimic']) })),
      })
      .nullable(),
    /** What the viewer may do right now. */
    options: z
      .object({
        propose: z.boolean(),
        vote: z.boolean(),
        mission: z.boolean(),
        /** Evil only: Fail is allowed. */
        canFail: z.boolean(),
        expose: z.boolean(),
      })
      .nullable(),
    /** The roles dealt (public: only how many of each). */
    setup: z.array(courtRoleSchema),
    /** Every player's role, revealed when the game is over. */
    roles: z.array(courtRoleSchema).nullable(),
    winner: z.enum(['good', 'evil']).nullable(),
    winReason: z.enum(['seer_survived', 'seer_found', 'missions_failed', 'rejections']).nullable(),
    /** Who the Hunter named. */
    target: playerIndex.nullable(),
  }),
  z.object({
    game: z.literal('bazaar'),
    /** Identifies this deal; the commitment's first eight hex digits as a number. */
    deal: z.number().int(),
    commitment: z.string(),
    /**
     * action: the mover picks one thing to do. discard: the mover is over the token
     * limit. patron: the mover chooses between visiting patrons. done: the turn's action is
     * finished and the mover presses Ready to pass on.
     */
    phase: z.enum(['action', 'discard', 'patron', 'done', 'over']),
    /** Turns taken so far by the player who moves first, plus one; the round number. */
    round: z.number().int().min(1),
    /** Someone has reached the target: the game ends when this round does. */
    finalRound: z.boolean(),
    /** Bank tokens: white, blue, green, red, black, wild. */
    bank: bazaarTokenCounts,
    /** Face-up card ids by level (index 0 is level 1); null where a deck ran dry. */
    market: z.array(z.array(z.number().int().nullable()).length(BAZAAR_MARKET_SLOTS)).length(3),
    deckCounts: z.array(z.number().int().min(0)).length(3),
    /** Patron ids still waiting for a visit. */
    patrons: z.array(z.number().int()),
    players: z.array(
      z.object({
        tokens: bazaarTokenCounts,
        /** Card ids bought, in order. */
        cards: z.array(z.number().int()),
        /** Discount per colour, from those cards. */
        bonuses: z.array(z.number().int()).length(5),
        patrons: z.array(z.number().int()),
        points: z.number().int(),
        /**
         * Stashed cards. A card taken face-up is public; one taken blind from a deck is
         * known only to its owner (id null for everyone else).
         */
        stashed: z.array(z.object({ level: z.number().int().min(1).max(3), card: z.number().int().nullable(), blind: z.boolean() })),
        /** Knocked out (timed out three turns running, or left). */
        out: z.boolean(),
      }),
    ),
    /** What the last player did, for the log line. */
    lastAction: z.object({ player: playerIndex, text: z.string().max(160) }).nullable(),
    /** What the viewer may do right now; null for spectators. */
    options: z
      .object({
        /** May take, stash or buy. */
        act: z.boolean(),
        /** May pass because nothing else is possible. */
        canPass: z.boolean(),
        /** Tokens to return to get down to the limit. */
        discard: z.number().int().min(0),
        /** Patrons to pick between. */
        patrons: z.array(z.number().int()),
        /** May press Ready to end the turn. */
        ready: z.boolean(),
      })
      .nullable(),
    winner: z.array(playerIndex).nullable(),
  }),
  z.object({
    game: z.literal('floatingbridge'),
    phase: z.enum(['bidding', 'calling', 'playing', 'done']),
    /** The viewer's own remaining cards, sorted; null for a spectator. */
    hand: z.array(cardCode).nullable(),
    /** Cards remaining in each player's hand (public). */
    handCounts: z.array(z.number().int().min(0).max(13)).length(4),
    /** The auction so far, in order; a pass is `number`/`strain` both null. */
    bids: z.array(
      z.object({
        player: playerIndex,
        number: z.number().int().min(1).max(7).nullable(),
        strain: z.enum(FLOATINGBRIDGE_STRAINS).nullable(),
      }),
    ),
    /** Whose turn to bid or pass; null once the auction has ended. */
    bidTurn: playerIndex.nullable(),
    declarer: playerIndex.nullable(),
    contract: z.object({ number: z.number().int().min(1).max(7), strain: z.enum(FLOATINGBRIDGE_STRAINS) }).nullable(),
    /** contract.number + 6; the tricks the declarer's side must win. */
    tricksNeeded: z.number().int().min(7).max(13).nullable(),
    /** The card the declarer named, public from the moment it is called. */
    calledCard: cardCode.nullable(),
    /**
     * The viewer's own teammate, known to them specifically: the declarer and
     * defenders don't know it until the called card is played (or the hand
     * ends), but the secret partner can see it in their own hand straight away.
     */
    partner: playerIndex.nullable(),
    /** Whether the floating partner is now public knowledge to everyone. */
    partnerRevealed: z.boolean(),
    /** Who leads the current trick. */
    leader: playerIndex.nullable(),
    /** Cards played to the trick in progress, in play order. */
    trick: z.array(z.object({ player: playerIndex, card: cardCode })),
    /** The trick just completed, for a beat before it clears. */
    lastTrick: z.object({ cards: z.array(z.object({ player: playerIndex, card: cardCode })), winner: playerIndex }).nullable(),
    /** Tricks won by each player so far (public; team totals are only as good as who you know is on your side). */
    tricksWon: z.array(z.number().int().min(0).max(13)).length(4),
    tricksPlayed: z.number().int().min(0).max(13),
    toAct: playerIndex.nullable(),
    /** Present only when it is the viewer's own turn to act, whatever the phase. */
    options: z
      .object({
        /** Cards you may legally play right now (follow-suit enforced); empty outside the playing phase. */
        legalCards: z.array(cardCode),
        /** Your own hand's points (bidding only; null otherwise). */
        points: z.number().int().nullable(),
        /** Bidding only: your hand is weak enough to call for a reshuffle instead of bidding or passing. */
        canReshuffle: z.boolean(),
      })
      .nullable(),
    /** Filled once the hand is done: the full reveal. */
    result: z
      .object({
        declarer: playerIndex,
        partner: playerIndex,
        contract: z.object({ number: z.number().int(), strain: z.enum(FLOATINGBRIDGE_STRAINS) }),
        tricksNeeded: z.number().int(),
        declarerTricks: z.number().int(),
        made: z.boolean(),
      })
      .nullable(),
  }),
]);
export type GameView = z.infer<typeof gameViewSchema>;
export type BazaarView = Extract<GameView, { game: 'bazaar' }>;
export type CourtView = Extract<GameView, { game: 'court' }>;
export type PokerView = Extract<GameView, { game: 'poker' }>;
export type MahjongView = Extract<GameView, { game: 'mahjong' }>;
export type FloatingBridgeView = Extract<GameView, { game: 'floatingbridge' }>;

/* -------------------------------------------------------------------------- */
/* Table state                                                                */
/* -------------------------------------------------------------------------- */

export const tableSeatSchema = z.object({
  userId: z.string(),
  name: z.string(),
  look: lookSchema,
  /** Out of the room; forfeits when the grace period runs out. */
  away: z.boolean(),
  /** A computer player ("Auntie"/"Uncle") filling an empty seat. */
  bot: z.boolean().optional(),
});
export type TableSeat = z.infer<typeof tableSeatSchema>;

export const tableStateSchema = z.object({
  itemId: z.string(),
  game: gameIdSchema,
  seats: z.array(tableSeatSchema.nullable()),
  phase: z.enum(['waiting', 'playing', 'finished']),
  /** The viewer's seat, if seated. */
  yourSeat: z.number().int().nullable(),
  /** Table seat of each player index for the game in progress (player 0 moves first). */
  order: z.array(z.number().int()),
  /** Table seat whose turn it is. */
  turn: z.number().int().nullable(),
  /** Server time when the current turn times out. */
  turnEndsAt: z.number().nullable(),
  /** Length of the current timer, for the timer bar (defaults to GAME_TURN_SECONDS). */
  turnMs: z.number().int().nullable().optional(),
  result: z
    .object({
      /** Winning table seats (empty for a draw). */
      winners: z.array(z.number().int()),
      reason: z.enum(['win', 'draw', 'resign', 'timeout', 'abandon']),
    })
    .nullable(),
  /** Seats that asked for a rematch. */
  rematch: z.array(z.number().int()),
  board: gameViewSchema.nullable(),
  moves: z.number().int(),
});
export type TableState = z.infer<typeof tableStateSchema>;

/* -------------------------------------------------------------------------- */
/* Messages                                                                   */
/* -------------------------------------------------------------------------- */

const itemId = z.string().min(1).max(64);

export const gameClientMessageSchema = z.discriminatedUnion('t', [
  /** Ask for the current state of the game at a table (and start watching it). */
  z.object({ t: z.literal('game:open'), itemId }),
  z.object({ t: z.literal('game:close'), itemId }),
  z.object({ t: z.literal('game:sit'), itemId, seat: z.number().int().min(0).max(COURT_MAX_PLAYERS - 1).optional() }),
  z.object({ t: z.literal('game:stand'), itemId }),
  /** Start (waiting) or ask for a rematch (finished). */
  z.object({ t: z.literal('game:start'), itemId }),
  z.object({ t: z.literal('game:move'), itemId, move: gameMoveSchema }),
  z.object({ t: z.literal('game:resign'), itemId }),
  /** Ask which tables in this room are free, waiting or playing; answered with `game:tables`. */
  z.object({ t: z.literal('game:list') }),
  /** Poker, mahjong and Shadow Court only: fill the empty seats with computer players. */
  z.object({ t: z.literal('game:bots'), itemId }),
]);

export const gameEventSchema = z.discriminatedUnion('kind', [
  /**
   * End of a hand/round: the exact deck or wall order and the salt, so anyone
   * can check sha256(`${salt}:${order.join(',')}`) against the commitment
   * published when the hand started.
   */
  z.object({
    kind: z.literal('reveal'),
    hand: z.number().int(),
    commitment: z.string(),
    salt: z.string(),
    order: z.array(z.string()).max(200),
  }),
  /** Something worth a callout ("Pong!", "Raise to 80", "Mahjong!"). */
  z.object({ kind: z.literal('announce'), seat: z.number().int(), text: z.string().max(60) }),
  z.object({ kind: z.literal('dice'), seat: z.number().int(), value: z.number().int().min(1).max(6) }),
  z.object({ kind: z.literal('moved'), seat: z.number().int() }),
  z.object({
    kind: z.literal('finished'),
    winners: z.array(z.number().int()),
    reason: z.enum(['win', 'draw', 'resign', 'timeout', 'abandon']),
  }),
]);
export type GameEvent = z.infer<typeof gameEventSchema>;

/** One table's status for the Games list, without opening it. */
export const tableSummarySchema = z.object({
  itemId: z.string(),
  game: gameIdSchema,
  /** Seats taken (bots included) and seats in all. */
  seated: z.number().int().min(0),
  seats: z.number().int().min(1),
  phase: z.enum(['waiting', 'playing', 'finished']),
});
export type TableSummary = z.infer<typeof tableSummarySchema>;

/** How a table looks to someone thinking of joining it. */
export type TableAvailability = 'free' | 'waiting' | 'full' | 'playing';

export function tableAvailability(table: Pick<TableSummary, 'seated' | 'seats' | 'phase'>): TableAvailability {
  if (table.phase === 'playing') return 'playing';
  if (table.seated >= table.seats) return 'full';
  return table.seated === 0 ? 'free' : 'waiting';
}

export const gameServerMessageSchema = z.discriminatedUnion('t', [
  /** Every game table in the player's room; the reply to `game:list`. */
  z.object({ t: z.literal('game:tables'), tables: z.array(tableSummarySchema).max(200) }),
  z.object({ t: z.literal('game:state'), state: tableStateSchema }),
  z.object({ t: z.literal('game:event'), itemId: z.string(), event: gameEventSchema }),
  z.object({ t: z.literal('game:closed'), itemId: z.string() }),
]);

export type GameClientMessage = z.infer<typeof gameClientMessageSchema>;
export type GameServerMessage = z.infer<typeof gameServerMessageSchema>;
